import React, {ClipboardEvent, FC, MouseEvent, PropsWithChildren} from 'react';

import {getWindow, isElement} from '../../../libs/shared/core/src/helpers/browser/domHelpers';

/*
 * Props.
 */

interface TextCopyProps {
  postProcess?: TextCopyPostProcessor;
}

type TextCopyPostProcessor = (text: string) => string;

/*
 * Component.
 */

export const TextCopy: FC<PropsWithChildren<TextCopyProps>> = ({postProcess, children}) => (
  <div onClickCapture={onClickCapture} onCopy={makeOnCopy(postProcess)}>
    {children}
  </div>
);

function onClickCapture(event: MouseEvent) {
  // Don't toggle or popover when the user is selecting things.
  if (event.target && isSelectionPresent(event.target)) {
    event.stopPropagation();
  }
}

function makeOnCopy(postProcess: TextCopyPostProcessor = (text) => text) {
  return (event: ClipboardEvent) => {
    const text = getSelectedText(event.target);
    if (!text) {
      return;
    }

    event.clipboardData.setData('text/plain', postProcess(text));
    event.preventDefault();
  };
}

function isSelectionPresent(target: EventTarget): boolean {
  if (!isElement(target)) {
    return false;
  }

  const currentWindow = getWindow(target);
  if (!currentWindow) {
    return false;
  }

  const selection = currentWindow.getSelection();
  if (!selection || !selection.rangeCount) {
    return false;
  }

  const range = selection.getRangeAt(0);
  if (range.collapsed) {
    return false;
  }

  const startNode = range.startContainer;
  return target.contains(startNode);
}

function getSelectedText(target: EventTarget): string {
  const currentWindow = getWindow(target);
  if (!currentWindow) {
    return '';
  }

  const selection = currentWindow.getSelection();
  if (!selection) {
    return '';
  }

  // Replace sequences of newlines and NBSPs resulting from divs by a simple space.
  return selection.toString();
}
